import { Col, Row, Spin } from 'antd';
import SuccessPurchaseImage from 'assets/images/success-purchase-image.png';
import SuccessPurchaseCard from 'components/SuccessPurchase/SuccessPurchaseCard';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PurchaseSteps from 'components/generalUI/PurchaseSteps';
import getMercadoPagoTokenSearchParams from 'utils/mercadopago/getMercadoPagoTokenSearchParams';
import { startPurchaseOnDate } from 'storage/purchaseStore';
import Places from 'components/sharedComponents/Places';
import ActualParkAvailability from 'components/sharedComponents/ActualParkAvailability';
import useGetPurchaseStatusQuery from 'apollo/resolvers/purchases/getStatus';
import { PurchaseState } from 'apollo/generated/types';
import styles from './SuccessPurchase.module.scss';

const PAYMENT_PROCESSING_WAIT_TIME = 5000;

const PurchaseSuccess: FC = () => {
  const { t } = useTranslation();

  const { purchaseId, date, email, uuid } = getMercadoPagoTokenSearchParams();
  const [paymentStatus, setPaymentStatus] = useState<PurchaseState | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [shouldQuery, setShouldQuery] = useState(false);

  const { data: purchaseData, loading } = useGetPurchaseStatusQuery(
    uuid || '',
    !shouldQuery
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldQuery(true);
    }, PAYMENT_PROCESSING_WAIT_TIME);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (purchaseData?.getPurchaseStatus?.id && purchaseData.getPurchaseStatus.state) {
      setPaymentStatus(purchaseData.getPurchaseStatus.state);
      setIsProcessing(false);
    }
  }, [purchaseData]);

  useEffect(() => {
    if (date) {
      startPurchaseOnDate(date);
    }
    sessionStorage.removeItem('selectedPurchase');
  }, [date]);

  if (isProcessing || loading) {
    return (
      <div className={styles.Container}>
        <div className={styles.Header}>
          <PurchaseSteps total={4} current={3} />
        </div>
        <Row justify="center">
          <Col>
            <h1 className={styles.Title}>{t('checkout.success.processingPayment')}</h1>
          </Col>
        </Row>
        <Row justify="center">
          <Col>
            <Spin size="large" />
          </Col>
        </Row>
      </div>
    );
  }

  const getStatusTitle = () => {
    switch (paymentStatus) {
      case PurchaseState.Confirmed:
        return t('checkout.success.title');
      case PurchaseState.Pending:
        return t('checkout.success.paymentPending');
      case PurchaseState.Rejected:
        return t('checkout.success.paymentRejected');
      case PurchaseState.Refunded:
        return t('checkout.success.paymentRefunded');
      default:
        return t('checkout.success.title');
    }
  };

  const isErrorState =
    paymentStatus === PurchaseState.Pending ||
    paymentStatus === PurchaseState.Rejected ||
    paymentStatus === PurchaseState.Refunded;

  if (isErrorState) {
    return (
      <div className={styles.Container}>
        <div className={styles.Header}>
          <PurchaseSteps total={4} current={3} />
        </div>
        <Row justify="center">
          <Col>
            <h1 className={styles.Title}>{getStatusTitle()}</h1>
          </Col>
        </Row>
        <Row justify="space-around">
          <Col span={24}>
            <img
              src={SuccessPurchaseImage}
              alt="SuccessPurchaseImage"
              className={styles.SuccessImage}
            />
          </Col>
        </Row>
        <Row justify="center" style={{ marginTop: '300px' }}>
          <Col span={20}>
            <p className={styles.ReminderText} style={{ fontSize: '18px' }}>
              {paymentStatus === PurchaseState.Refunded ? (
                <>
                  {t('checkout.success.refundDescription')}
                  <br />
                  {t('checkout.success.ContactText')}
                </>
              ) : (
                t('checkout.success.ContactText')
              )}
            </p>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className={styles.Container}>
      <div className={styles.Header}>
        <PurchaseSteps total={4} current={3} />
      </div>
      <Row justify="center">
        <Col>
          <h1 className={styles.Title}>{getStatusTitle()}</h1>
        </Col>
      </Row>
      <Row justify="space-around">
        <Col span={24}>
          <img
            src={SuccessPurchaseImage}
            alt="SuccessPurchaseImage"
            className={styles.SuccessImage}
          />
        </Col>
      </Row>
      <Row justify="space-around">
        <SuccessPurchaseCard
          purchaseId={purchaseId}
          email={email || ''}
          uuid={uuid || ''}
        />
      </Row>
      <Places />
      <ActualParkAvailability date={date || undefined} />
    </div>
  );
};

export default PurchaseSuccess;
